export type CoiStatus = 'green' | 'yellow' | 'red' | 'none';

export interface CoiStatusResult {
  status: CoiStatus;
  reason: string;
}

/** Traffic-light status derived purely from an expiration date: green (>90 days left), yellow
 * (<=90 days), red (<=30 days or already expired), none (nothing on file / no date extracted yet).
 * A pure function so every badge that uses it (CoI, License) can never disagree on the thresholds.
 * Expects the document's own MM/DD/YYYY (or M/D/YYYY) string form. `noneReason` customizes the
 * "nothing on file" message per document type (CoI vs. License). */
export function computeExpiryStatus(
  expirationDate: string | undefined,
  now: Date = new Date(),
  noneReason = 'Nothing on file',
): CoiStatusResult {
  if (!expirationDate) {
    return { status: 'none', reason: noneReason };
  }
  const match = expirationDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) {
    return { status: 'none', reason: 'Expiration date could not be parsed' };
  }
  const [, month, day, year] = match;
  const exp = new Date(Number(year), Number(month) - 1, Number(day));
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysLeft = Math.round((exp.getTime() - now.getTime()) / msPerDay);

  if (daysLeft < 0) {
    return { status: 'red', reason: `Expired ${-daysLeft} day${-daysLeft === 1 ? '' : 's'} ago (${expirationDate})` };
  }
  if (daysLeft <= 30) {
    return { status: 'red', reason: `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${expirationDate})` };
  }
  if (daysLeft <= 90) {
    return { status: 'yellow', reason: `Expires in ${daysLeft} days (${expirationDate})` };
  }
  return { status: 'green', reason: `Valid through ${expirationDate} (${daysLeft} days left)` };
}

/** Certificate-of-Insurance-flavored wrapper around computeExpiryStatus — kept as its own named
 * export since it's already wired into ContractorsPage/ContractorDetailPage. */
export function computeCoiStatus(expirationDate: string | undefined, now: Date = new Date()): CoiStatusResult {
  return computeExpiryStatus(expirationDate, now, 'No Certificate of Insurance on file');
}

/** License-flavored wrapper — same thresholds, license-specific "nothing on file" message. */
export function computeLicenseStatus(expirationDate: string | undefined, now: Date = new Date()): CoiStatusResult {
  return computeExpiryStatus(expirationDate, now, 'No expiration date on file');
}
